import { StateSectionPolyline } from 'data/model/routePlanner'
import { setSelectedRoute } from 'data/reducer/routePlanner'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import { mapConfig } from 'lib/config'
import { createHighlightableMarker } from 'map/markers'
import React, { useEffect, useRef } from 'react'
import ReactDOMServer from 'react-dom/server'

interface MapRoutesLayerProps {
  map: H.Map
  highlightedId?: string
  onHover?: (id?: string) => void
}
type LineType = 'border' | 'polyline'

interface Event {
  target: Target
}

interface Target {
  data: RouteLineData
}

interface RouteLineData {
  routeId: string
  sectionId: string
  type: LineType
}

interface MarkerData {
  id: string
}

// TODO don't pass map entities
export default function MapRoutesLayer({ map, highlightedId, onHover }: MapRoutesLayerProps): null {
  const dispatch = useAppDispatch()
  const groupRef = useRef<H.map.Group | null>(null)
  const routes = useAppSelector(({ routePlanner }) =>
    routePlanner.selectedRoute ? [routePlanner.selectedRoute] : routePlanner.routes
  )
  const selectedRoute = useAppSelector(({ routePlanner }) => routePlanner.selectedRoute)

  useEffect(() => {
    const group = new H.map.Group()
    routes.forEach((route) => {
      // const isActive = route.id === selected.id
      route.sections.map((section, index) => {
        const linestring = H.geo.LineString.fromFlexiblePolyline(section.polyline.value)
        const border = new H.map.Polyline(linestring, {
          data: { routeId: route.id, sectionId: section.id, type: 'border' } as RouteLineData,
          style: getLineStyle('border', false, route.sections[index].polyline),
        } as H.map.Spatial.Options)
        const polyline = new H.map.Polyline(linestring, {
          data: { routeId: route.id, sectionId: section.id, type: 'polyline' } as RouteLineData,
          style: getLineStyle('polyline', false, route.sections[index].polyline),
        } as H.map.Spatial.Options)
        const source = section.departure.from
        const destination = section.arrival.at
        const startMarker = createHighlightableMarker(
          { ...source.location },
          endPointMarker(route.sections[index].polyline, true),
          route.id,
          2
        )
        const endMarker = createHighlightableMarker(
          { ...destination },
          endPointMarker(route.sections[index].polyline, true),
          route.id,
          2
        )

        const handleMouseEnter = ({ target }: Event): void => {
          onHover && onHover(target.data.routeId)
          document.body.style.cursor = 'pointer'
        }

        const handleMouseLeave = (): void => {
          onHover && onHover(undefined)
          document.body.style.cursor = 'default'
        }

        const handleOnClick = (): void => {
          dispatch(setSelectedRoute(route))
        }

        polyline.addEventListener('tap', handleOnClick)
        polyline.addEventListener('pointerenter', handleMouseEnter)
        polyline.addEventListener('pointerleave', handleMouseLeave)
        border.addEventListener('tap', handleOnClick)
        border.addEventListener('pointerenter', handleMouseEnter)
        border.addEventListener('pointerleave', handleMouseLeave)
        startMarker.addEventListener('tap', handleOnClick)
        startMarker.addEventListener('pointerenter', handleMouseEnter)
        startMarker.addEventListener('pointerleave', handleMouseLeave)
        endMarker.addEventListener('tap', handleOnClick)
        endMarker.addEventListener('pointerenter', handleMouseEnter)
        endMarker.addEventListener('pointerleave', handleMouseLeave)

        group.addObject(startMarker)
        group.addObject(endMarker)
        // Add the polyline to the map
        group.addObject(border)
        group.addObject(polyline)
      })
    })

    groupRef.current = group

    map.addObject(group)
    // And zoom to its bounding rectangle
    map.getViewModel().setLookAtData({
      bounds: group.getBoundingBox(),
    })

    return () => {
      if (groupRef.current) {
        map.removeObject(groupRef.current as H.map.Object)
      }
    }
  }, [map, dispatch, routes, onHover])

  useEffect(() => {
    const selected = routes.find((route) => route.id === highlightedId) || routes[0]
    if (groupRef.current !== null) {
      const lineObjects = groupRef.current.getObjects()
      lineObjects.forEach((lineObject) => {
        const { routeId, sectionId, type } = lineObject.getData() as RouteLineData
        const { id } = lineObject.getData() as MarkerData
        const isMarkerActive = id === selected.id
        if (id !== undefined) {
          ;(lineObject as H.map.DomMarker).setVisibility(isMarkerActive)
        }
        if (type !== undefined) {
          const isLineActive = routeId === selected.id
          selected.sections.map((section) => {
            ;(lineObject as H.map.Polyline).setStyle(
              getLineStyle(type, isLineActive, section.polyline)
            )
            ;(lineObject as H.map.Polyline).setZIndex(isLineActive ? 10 : 0)
          })
          const currentSection = selected.sections.find((section) => section.id === sectionId)
          if (currentSection !== undefined) {
            ;(lineObject as H.map.Polyline).setStyle(
              getLineStyle(type, isLineActive, currentSection.polyline)
            )
            ;(lineObject as H.map.Polyline).setZIndex(isLineActive ? 10 : 0)
          }
        }
      })
    }
  }, [highlightedId, routes, selectedRoute])
  return null
}

function getLineStyle(
  lineType: LineType,
  isActive: boolean,
  polyline: StateSectionPolyline
): H.map.SpatialStyle.Options {
  const lineWidth = 7
  return {
    lineWidth: lineType === 'border' ? lineWidth + 2 : lineWidth,
    ...(lineType === 'border' && {
      strokeColor: getBorderLineStrokeColor(polyline, isActive),
    }),
    ...(lineType === 'polyline' && {
      strokeColor: getPolyLineStrokeColor(polyline, isActive),
    }),
  }
}

function getBorderLineStrokeColor(polyline: StateSectionPolyline, isActive: boolean): string {
  if (isActive) {
    return polyline.color.stroke
  }
  return '#939eb2'
}

function getPolyLineStrokeColor(polyline: StateSectionPolyline, isActive: boolean): string {
  if (isActive) {
    return polyline.color.main
  }
  return '#B8BFCC'
}

function endPointMarker(polyline: StateSectionPolyline, isActive: boolean): string {
  const { dimensions, style } = mapConfig.markers.routeEndPoints.normal
  const fillColor = getPolyLineStrokeColor(polyline, isActive)
  const borderColor = getBorderLineStrokeColor(polyline, isActive)
  return ReactDOMServer.renderToStaticMarkup(
    <svg {...dimensions} viewBox="0 0 20 20" style={style} fill="none">
      <circle cx="10" cy="10" r="9" fill={fillColor} />
      <circle cx="10" cy="10" r="8" stroke={borderColor} strokeWidth="1" />
      <circle cx="10" cy="10" r="4" fill="white" />
    </svg>
  )
}
